/**
 * Auto-generate breadcrumbs from a pathname using module definitions.
 *
 * Given "/accounting/journal/JE-001", produces:
 * [{ label: "Бухгалтерия", href: "/accounting" },
 *  { label: "Журнал", href: "/accounting/journal" },
 *  { label: "JE-001" }]
 */

import type { Crumb } from "./use-breadcrumbs";
import { getModuleDefs } from "./workspaces";

/** i18n keys for known path segments. */
const SEGMENT_KEYS: Record<string, string> = {
	accounting: "nav:accounting",
	journal: "accounting:nav_journal",
	accounts: "accounting:nav_accounts",
	bank: "accounting:nav_bank",
	assets: "accounting:nav_assets",
	budget: "accounting:nav_budget",
	close: "accounting:nav_close",
	analysis: "accounting:nav_analysis",
	products: "nav:products",
	categories: "nav:categories",
	commerce: "nav:commerce",
	orders: "nav:orders",
	quotes: "nav:quotes",
	portals: "nav:portals",
	payments: "nav:payments",
	delivery: "nav:delivery",
	subscriptions: "nav:subscriptions",
	fulfillment: "nav:fulfillment",
	parties: "nav:contacts",
	activity: "nav:activity",
	diagnostics: "nav:diagnostics",
	settings: "nav:settings",
	admin: "nav:admin",
	assistant: "nav:assistant",
	general: "settings:sidebar_general",
	appearance: "settings:sidebar_appearance",
	notifications: "settings:sidebar_notifications",
	plugins: "settings:sidebar_plugins",
	roles: "settings:sidebar_roles",
	members: "settings:sidebar_members",
	tokens: "settings:sidebar_tokens",
	webhooks: "settings:sidebar_webhooks",
	catalog: "settings:sidebar_catalog",
	tags: "settings:sidebar_tags",
	diagram: "settings:sidebar_diagram",
	organization: "settings:sidebar_organization",
	subscription: "settings:sidebar_subscription",
	manufacturing: "nav:manufacturing",
};

export function generateBreadcrumbs(
	pathname: string,
	pageTitle?: string,
	t?: (key: string, opts?: { defaultValue?: string }) => string,
): Crumb[] {
	const segments = pathname.split("/").filter(Boolean);
	if (segments.length === 0) return [];

	const resolve = (key: string, fallback: string) =>
		t ? t(key, { defaultValue: fallback }) : fallback;

	const crumbs: Crumb[] = [];
	let currentPath = "";

	for (let i = 0; i < segments.length; i++) {
		const segment = segments[i];
		currentPath += `/${segment}`;
		const isLast = i === segments.length - 1;

		let label: string | undefined;

		// Try i18n key
		const i18nKey = SEGMENT_KEYS[segment];
		if (i18nKey) {
			label = resolve(i18nKey, segment);
		}

		// Try module defs
		if (!label) {
			const defs = getModuleDefs();
			const mod = defs.find((m) => m.prefix === currentPath);
			if (mod) {
				label = resolve(mod.labelKey, segment);
			}
		}

		// For the last segment: use pageTitle if it looks like a dynamic param
		if (isLast && pageTitle && !label) {
			label = pageTitle;
		}

		// Fallback
		if (!label) {
			label = segment;
		}

		crumbs.push({
			label,
			href: isLast ? undefined : currentPath,
		});
	}

	return crumbs;
}
